import {
    ChangeEvent,
    FC,
    useEffect,
    useLayoutEffect,
    useState,
    useCallback,
    useMemo,
} from "react";
import "../../assets/components/Table/Table_header.scss";
import "./IoloLicenseKeys.scss";
import {
    useGetIoloLicenseInfoQuery,
    useImportIoloLicenseMutation,
    IoloLicenseInfoQueryParams,
    IoloDetailInfo,
} from "../../redux/RTKQuery/api";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import { AccessRoles } from "../../models/models";
import { Loading, uiSlicedActions } from "../../redux/ui-slice";
import Loader from "../../assets/components/Loader/Loader";
import { Page } from "../../assets/components/PaginationBar/PaginationBar";
import PageTitle from "../../assets/components/PageTitle/PageTitle";
import IoloLicenseKeysTableHeader from "./IoloLicenseKeysTable/IoloLicenseKeysTableHeader";
import IoloLicenseKeysTable from "./IoloLicenseKeysTable/IoloLicenseKeysTable";
import IoloLicenseKeysSearchBar from "./IoloLicenseKeysTable/IoloLicenseKeysSearchBar";
import { SearchBarDataHandler } from "../../assets/components/SearchBar/SearchBar";
import { toast } from "react-toastify";

const mapQuiresToIoloParams = (
    quires: SearchBarDataHandler,
): Omit<IoloLicenseInfoQueryParams, "pageNumber" | "pageSize"> => ({
    LicenseKey: quires.searchInput || undefined,
    OrderId: quires.searchByOrderId || undefined,
    ParsedStatus: quires.status,
    StartDate: quires.startDate,
    EndDate: quires.endDate,
    NextRebillStartDate: quires.nextRebillStartDate,
    NextRebillEndDate: quires.nextRebillEndDate,
    sortNextRebillDate: quires.sortNextRebillDate,
});

const IoloLicenseKeys: FC = () => {
    const dispatch = useAppDispatch();
    const loading: Loading = useAppSelector((state) => state.ui.loading);
    const role = useAppSelector((state) => state.initializeApp.role);
    const pageTitle =
        role === AccessRoles.SUPPORT ? "License Keys" : "Iolo License Keys";

    const pageSize: number = 10;
    const [currentSizePage, setCurrentSizePage] = useState(pageSize);
    const [pageNumber, setPageNumber] = useState<Page>(1);
    const [dataTable, setData] = useState<IoloDetailInfo[]>([]);
    const [totalLicenses, setTotalLicenses] = useState<number>(0);
    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});

    const queryArgs = useMemo<IoloLicenseInfoQueryParams>(
        () => ({
            pageNumber,
            pageSize,
            ...mapQuiresToIoloParams(quiresData),
        }),
        [pageNumber, pageSize, quiresData],
    );

    const {
        data: dataLicenses,
        isSuccess: isSuccessLicenses,
        isError: isErrorLicenses,
        isFetching: isFetchingLicenses,
    } = useGetIoloLicenseInfoQuery(queryArgs, { refetchOnFocus: true });

    // Set page number to 1 when filters change
    useEffect(() => {
        setPageNumber(1);
    }, [quiresData]);

    const [importIoloLicense, { isLoading: isImporting }] =
        useImportIoloLicenseMutation();

    const onImportCSV = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        const formData = new FormData();
        formData.append("File", file);

        try {
            const result = await importIoloLicense(formData).unwrap();
            const count = result.importedCount ?? 0;
            toast.success(
                result.message ?? `Imported ${count} license(s)`,
            );
        } catch (err) {
            const data = (err as {
                data?: { message?: string; errors?: string[] };
            })?.data;
            const lines = [
                data?.message ?? "Failed to import licenses",
                ...(data?.errors ?? []),
            ];
            toast.error(lines.join("\n"));
        }
    };

    const handleLicensesData = useCallback(() => {
        if (isSuccessLicenses && dataLicenses) {
            setData(dataLicenses.detailedInfo ?? []);
            if (dataLicenses.filteredLicensesCount !== undefined) {
                setCurrentSizePage(() => {
                    if (totalLicenses < pageSize) {
                        return dataLicenses.filteredLicensesCount as number;
                    } else {
                        return pageSize;
                    }
                });
                setTotalLicenses(dataLicenses.filteredLicensesCount ?? 0);
            } else {
                setTotalLicenses(dataLicenses.totalLicenses ?? 0);
            }
        }
        if (isErrorLicenses) {
            toast.error("Error loading license keys");
        }
    }, [
        isSuccessLicenses,
        dataLicenses,
        totalLicenses,
        pageSize,
        isErrorLicenses,
    ]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal = isFetchingLicenses || isImporting;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden"),
        );
    }, [isFetchingLicenses, isImporting, dispatch]);

    // Handle data fetching and state updates
    useLayoutEffect(() => {
        handleLicensesData();
        handleLoadingState();
    }, [handleLicensesData, handleLoadingState]);

    return (
        <div className="page_with_table">
            <Loader loading={loading} />
            <PageTitle text={pageTitle} />
            <IoloLicenseKeysSearchBar dataHandler={setQuires} />
            <div className="table_scroll_wrapper">
                <IoloLicenseKeysTableHeader
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    dataTable={dataTable}
                    totalLicenses={totalLicenses}
                    setCurrentPage={setPageNumber}
                    currentSizePage={currentSizePage}
                    onImportCSV={onImportCSV}
                    isImporting={isImporting}
                />
                <IoloLicenseKeysTable dataTable={dataTable} onSort={setQuires} />
            </div>
        </div>
    );
};

export default IoloLicenseKeys;
